import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Engraved, Icon, Script, TopBar } from "../components/ui";
import type { IconName, ScreenId } from "../components/ui";
import { FloralSpray } from "../components/Florals";
import type { FloralDensity } from "../components/Florals";
import { PhotoTile } from "../components/PhotoTile";
import { washFor } from "../lib/content";
import { useEvent } from "../lib/event";
import { api, putToR2 } from "../lib/api";
import { downloadAll, sharePhotos } from "../lib/download";
import type { PhotoDTO } from "@wedding/shared";

type FindStep = "intro" | "camera" | "scanning" | "results" | "error";

const INTRO_STEPS: [IconName, string][] = [
  ["camera", "Snap a selfie"],
  ["sparkle", "We match faces"],
  ["heart", "Your gallery, ready"],
];

export function FindMeScreen({ go, density }: { go: (s: ScreenId) => void; density: FloralDensity }) {
  const { eventId, live } = useEvent();
  const [step, setStep] = useState<FindStep>("intro");
  const [facing, setFacing] = useState<"user" | "environment">("user");
  const [matches, setMatches] = useState<(PhotoDTO & { score: number })[]>([]);
  const [mockFound, setMockFound] = useState(0);
  const [errMsg, setErrMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const total = 1248;

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const startCamera = useCallback(
    async (mode: "user" | "environment") => {
      try {
        stopCamera();
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode }, audio: false });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
      } catch {
        setErrMsg("We couldn't open your camera. Allow camera access in your browser and try again.");
        setStep("error");
      }
    },
    [stopCamera],
  );

  // Manage the live camera stream while on the camera step.
  useEffect(() => {
    if (!live) return;
    if (step === "camera") startCamera(facing);
    else stopCamera();
    return () => stopCamera();
  }, [live, step, facing, startCamera, stopCamera]);

  // Mock scanning animation (non-live).
  useEffect(() => {
    if (live || step !== "scanning") return;
    const t = setTimeout(() => {
      setMockFound(23);
      setStep("results");
    }, 2600);
    return () => clearTimeout(t);
  }, [live, step]);

  async function runSearch(blob: Blob) {
    if (!eventId) return;
    try {
      const { uploadUrl, headers, objectKey } = await api.selfieUrl({
        eventId,
        contentType: "image/jpeg",
      });
      await putToR2(uploadUrl, headers, blob);
      const r = await api.searchFace({ eventId, selfieObjectKey: objectKey });
      setMatches(r.matches);
      setStep("results");
    } catch {
      setErrMsg("Something went wrong finding your photos. Please try again.");
      setStep("error");
    }
  }

  async function capture() {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth || 720;
    const h = video.videoHeight || 960;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    stopCamera();
    setStep("scanning");
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", 0.9));
    if (!blob) {
      setErrMsg("We couldn't capture that. Please try again.");
      setStep("error");
      return;
    }
    await runSearch(blob);
  }

  // Save / share the matched photos (live mode only — mock tiles have no real URLs).
  async function saveAll() {
    if (saving || !matches.length) return;
    setSaving(true);
    try {
      await downloadAll(matches.map((m) => m.fullUrl));
    } finally {
      setSaving(false);
    }
  }

  async function shareAll() {
    if (saving || !matches.length) return;
    setSaving(true);
    try {
      await sharePhotos(
        matches.map((m) => m.fullUrl),
        { title: "My wedding photos" },
      );
    } finally {
      setSaving(false);
    }
  }

  // ---------------- INTRO ----------------
  if (step === "intro") {
    return (
      <div className="screen findme-intro">
        <TopBar title="Find Photos Of You" onBack={() => go("home")} />
        <FloralSpray variant="topBorder" density={density} width={400} height={120} seed={31} className="fm-garland" />
        <div className="fm-body">
          <div className="fm-selfie-ph">
            <Icon name="face" size={56} stroke={1.2} />
          </div>
          <Script size={40} style={{ color: "var(--c-accent)" }}>
            Where am I?
          </Script>
          <p className="fm-copy">
            Take one selfie and we'll find every photo you appear in — across everything guests upload. New matches keep
            arriving through the night.
          </p>
          <div className="fm-steps">
            {INTRO_STEPS.map(([ic, tx], i) => (
              <div className="fm-step" key={i}>
                <span className="fm-step-ic">
                  <Icon name={ic} size={18} />
                </span>
                <span>{tx}</span>
              </div>
            ))}
          </div>
          <button className="btn primary wide" onClick={() => setStep("camera")}>
            <Icon name="camera" size={18} stroke={1.8} style={{ marginRight: 8 }} />
            Take a selfie
          </button>
          <Engraved size={10} style={{ color: "var(--c-ink-soft)", letterSpacing: "0.2em", marginTop: 14 }}>
            Your selfie is only used to find your photos
          </Engraved>
        </div>
      </div>
    );
  }

  // ---------------- CAMERA ----------------
  // Rendered via a portal into the app shell (a sibling of the bottom nav) so it isn't
  // trapped beneath the nav by iOS Safari's -webkit-overflow-scrolling stacking layer.
  if (step === "camera") {
    const cameraUI = (
      <div className="screen fm-camera">
        <div className="vf" style={live ? undefined : washFor(777)}>
          {live ? (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="vf-video"
              style={{ transform: facing === "user" ? "scaleX(-1)" : "none" }}
            />
          ) : (
            <div className="vf-grain" />
          )}
          <div className="vf-oval" />
          <Engraved size={11} className="vf-hint" style={{ color: "#fff" }}>
            Center your face in the ring
          </Engraved>
        </div>
        <div className="vf-controls">
          <button className="vf-cancel" onClick={() => setStep("intro")}>
            Cancel
          </button>
          <button className="shutter" onClick={() => (live ? capture() : setStep("scanning"))} aria-label="Capture">
            <span />
          </button>
          <button
            className="vf-flip"
            aria-label="Flip"
            onClick={() => setFacing((f) => (f === "user" ? "environment" : "user"))}
          >
            <Icon name="refresh" size={20} />
          </button>
        </div>
      </div>
    );
    return createPortal(cameraUI, document.getElementById("wp-app") ?? document.body);
  }

  // ---------------- SCANNING ----------------
  if (step === "scanning") {
    return (
      <div className="screen fm-scan">
        <div className="scan-card">
          <div className="scan-face" style={washFor(777)}>
            <div className="scan-sweep" />
            <div className="scan-bracket tl" />
            <div className="scan-bracket tr" />
            <div className="scan-bracket bl" />
            <div className="scan-bracket br" />
          </div>
        </div>
        <Script size={34} style={{ color: "var(--c-accent)" }}>
          Looking for you
        </Script>
        <Engraved size={11.5} style={{ color: "var(--c-ink-soft)" }}>
          Searching {total.toLocaleString()} photos…
        </Engraved>
        <div className="scan-dots">
          <span />
          <span />
          <span />
        </div>
      </div>
    );
  }

  // ---------------- ERROR ----------------
  if (step === "error") {
    return (
      <div className="screen fm-scan">
        <FloralSpray variant="sprig" density={density} width={180} height={130} seed={19} />
        <Script size={34} style={{ color: "var(--c-accent)" }}>
          Hmm
        </Script>
        <p className="fm-copy">{errMsg}</p>
        <button className="btn primary" onClick={() => setStep("intro")}>
          Try again
        </button>
      </div>
    );
  }

  // ---------------- RESULTS ----------------
  const found = live ? matches.length : mockFound;
  const mockTiles = Array.from({ length: mockFound }, (_, i) => 500 + i);
  return (
    <div className="screen fm-results">
      <TopBar
        title="Your Photos"
        onBack={() => go("home")}
        right={
          live && matches.length > 0 ? (
            <button className="iconbtn" aria-label="Save all" onClick={saveAll} disabled={saving}>
              <Icon name="download" size={20} />
            </button>
          ) : undefined
        }
      />
      <div className="fmr-head">
        <Script size={38} style={{ color: "var(--c-accent)" }}>
          {found ? "Found you" : "No matches yet"}
        </Script>
        <p className="fmr-sub">
          {found ? (
            <>
              <strong>{found} photos</strong> of you so far. We'll add more as the night goes on.
            </>
          ) : (
            <>We couldn't spot you in the album yet — check back as more photos come in.</>
          )}
        </p>
        {live && matches.length > 0 && (
          <div className="fmr-actions">
            <button className="btn primary" onClick={saveAll} disabled={saving}>
              <Icon name="download" size={16} stroke={1.9} style={{ marginRight: 7 }} />
              {saving ? "Saving…" : "Save all"}
            </button>
            <button className="btn ghost" onClick={shareAll} disabled={saving}>
              <Icon name="share" size={16} stroke={1.7} style={{ marginRight: 7 }} />
              Share
            </button>
          </div>
        )}
      </div>
      <div className="fmr-grid">
        {live
          ? matches.map((m) => <PhotoTile key={m.photoId} src={m.thumbUrl} ratio="1 / 1" radius={5} />)
          : mockTiles.map((s) => <PhotoTile key={s} seed={s} ratio="1 / 1" radius={5} />)}
      </div>
      {found > 0 && (
        <div className="fmr-live">
          <span className="live-dot" />
          <Engraved size={10.5} style={{ color: "var(--c-ink-soft)", letterSpacing: "0.2em" }}>
            Live · new matches appear automatically
          </Engraved>
        </div>
      )}
    </div>
  );
}
